import * as HoverCard from "@radix-ui/react-hover-card";
import { Flex, Text, Button } from "@radix-ui/themes";
import { useCallback, useEffect, useState } from "react";
import { FaChevronRight } from "react-icons/fa";

import { useTracksControllerTracksListMoods } from "@/lib/lutely-api/gen/tracks/tracks";
import { marketplaceTokens } from "@/lib/tokens";
import { cn } from "@/lib/utils";
import FilterCheckbox from "./shared/FilterCheckbox";
import FilterFooter from "./shared/FilterFooter";
import useFilterDropdown from "./shared/useFilterDropdown";
import { MarketplaceFilterKey, MarketplaceUpdates } from "../types";

type MoodsFilterProps = {
  parameter?: string;
  onSetFilter: (updates: MarketplaceUpdates) => void;
  onRemoveFilter: (param: MarketplaceFilterKey) => void;
};

export default function MoodsFilter({
  parameter,
  onSetFilter,
  onRemoveFilter,
}: MoodsFilterProps) {
  const { data: moodsData } = useTracksControllerTracksListMoods();
  const [selectedMoods, setSelectedMoods] = useState<string[]>([]);

  const restoreFromParameter = useCallback(() => {
    if (parameter) {
      setSelectedMoods(parameter.split(", "));
    } else {
      setSelectedMoods([]);
    }
  }, [parameter]);

  const {
    open,
    isPinned,
    handleOpenChange,
    handleTriggerClick,
    handleInteractOutside,
    close,
  } = useFilterDropdown({ id: "moods", onRestore: restoreFromParameter });

  useEffect(() => {
    restoreFromParameter();
  }, [parameter, restoreFromParameter]);

  function toggleMood(moodId: string) {
    const updated = [...selectedMoods];
    const index = updated.indexOf(moodId);

    if (index >= 0) {
      updated.splice(index, 1);
    } else {
      updated.push(moodId);
    }

    setSelectedMoods(updated);
  }

  function handleApply() {
    if (selectedMoods.length === 0) {
      onRemoveFilter("mood");
    } else {
      onSetFilter({ mood: selectedMoods.join(", ") });
    }
    close({ restore: false });
  }

  function handleReset() {
    setSelectedMoods([]);
    onRemoveFilter("mood");
    close({ restore: false });
  }

  // Split moods into two columns
  const midpoint = moodsData ? Math.ceil(moodsData.length / 2) : 0;
  const leftColumn = moodsData?.slice(0, midpoint) || [];
  const rightColumn = moodsData?.slice(midpoint) || [];

  return (
    <HoverCard.Root
      open={open}
      onOpenChange={handleOpenChange}
      openDelay={isPinned ? 0 : 200}
      closeDelay={isPinned ? 0 : 100}
    >
      <HoverCard.Trigger asChild>
        <Button
          onClick={handleTriggerClick}
          className={cn(
            "flex items-center gap-[8px] text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 focus-visible:ring-offset-black",
            marketplaceTokens.filterItem,
            parameter && "!text-white !bg-background-neutral-inverted",
            {
              [marketplaceTokens.filterItemActive]: open,
              [marketplaceTokens.filterItemInactive]: !open,
            },
          )}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-pressed={isPinned}
        >
          <Text className="flex-1">Moods</Text>
          <FaChevronRight
            size={10}
            aria-hidden
            className={cn("transition-transform", {
              "rotate-90": open,
            })}
          />
        </Button>
      </HoverCard.Trigger>

      <HoverCard.Portal>
        <HoverCard.Content
          side="bottom"
          avoidCollisions={false}
          align="start"
          sideOffset={2}
          className="w-[350px] bg-background-neutral-inverted rounded shadow-[0px_10px_18px_-1px_rgba(5,12,43,0.14)] pt-4 px-4 pb-3 flex flex-col gap-4 z-50"
          onInteractOutside={handleInteractOutside}
        >
          {/* Two-column grid */}
          <Flex className="gap-4">
            {/* Left Column */}
            <Flex direction="column" className="flex-1 gap-[2px]">
              {leftColumn.map((mood) => {
                const isSelected = selectedMoods.includes(
                  mood.classificationId,
                );
                return (
                  <Flex
                    key={mood.classificationId}
                    onClick={() => toggleMood(mood.classificationId)}
                    className={cn(
                      "h-[34px] px-[8px] rounded flex items-center gap-[8px] cursor-pointer text-white hover:bg-[#3A404D]/60 hover:text-teal-500",
                      {
                        "bg-[#3A404D]/60": isSelected,
                      },
                    )}
                  >
                    <FilterCheckbox checked={isSelected} />
                    <Text
                      className={cn("text-[14px] font-semibold", {
                        "text-teal-500": isSelected,
                      })}
                    >
                      {mood.name}
                    </Text>
                  </Flex>
                );
              })}
            </Flex>

            {/* Right Column */}
            <Flex direction="column" className="flex-1 gap-[2px]">
              {rightColumn.map((mood) => {
                const isSelected = selectedMoods.includes(
                  mood.classificationId,
                );
                return (
                  <Flex
                    key={mood.classificationId}
                    onClick={() => toggleMood(mood.classificationId)}
                    className={cn(
                      "h-[34px] px-[8px] rounded flex items-center gap-[8px] cursor-pointer text-white hover:bg-[#3A404D]/60 hover:text-teal-500",
                      {
                        "bg-[#3A404D]/60": isSelected,
                      },
                    )}
                  >
                    <FilterCheckbox checked={isSelected} />
                    <Text
                      className={cn("text-[14px] font-semibold", {
                        "text-teal-500": isSelected,
                      })}
                    >
                      {mood.name}
                    </Text>
                  </Flex>
                );
              })}
            </Flex>
          </Flex>

          {/* Footer */}
          <FilterFooter onReset={handleReset} onApply={handleApply} />
        </HoverCard.Content>
      </HoverCard.Portal>
    </HoverCard.Root>
  );
}
